import React, { createContext, ReactNode, useContext, useEffect } from 'react';

import { initializeClientConfig } from '../helpers/fetch';
import { getRuntimeVariables } from '../helpers/runtimeVariables';

type RuntimeConfig = Awaited<ReturnType<typeof getRuntimeVariables>>;

const RuntimeConfigContext = createContext<RuntimeConfig | null>(null);

export const RuntimeConfigProvider = ({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) => {
  // Initialize the client-side config cache for the fetch helpers synchronously
  // This is important for tests where useEffect might not run before components try to use the config
  if (typeof window !== 'undefined') {
    initializeClientConfig(config);
  }

  // Also initialize in useEffect to handle SSR and ensure it's set after hydration
  useEffect(() => {
    initializeClientConfig(config);
  }, [config]);

  return (
    <RuntimeConfigContext.Provider value={config}>
      {children}
    </RuntimeConfigContext.Provider>
  );
};

export const useRuntimeConfig = () => {
  const context = useContext(RuntimeConfigContext);
  if (!context) {
    throw new Error(
      'useRuntimeConfig must be used within a RuntimeConfigProvider'
    );
  }
  return context;
};
